/**
 * Bilingual (TH/EN) error mapping for the "Book me a caddie" (tip-offer) create
 * flow. The backend returns typed errors shaped as:
 *
 *   { success: false, error: { code, message: { en, th } } }
 *
 * (see golfalo-be src/utils/errors/BaseError.js -> toResponse()).
 *
 * We prefer the backend-provided Thai message when present (it already contains
 * context such as the configured tip bound), and fall back to a local bilingual
 * message keyed off the recognizable validation cases required by the spec
 * (missing field, past tee time, tip out of bounds).
 *
 * Requirements: 9.2, 9.9, 9.10
 */

/**
 * Local fallback messages for the create-offer validation cases, keyed by a
 * coarse reason. Bilingual TH (primary) + EN, matching the app's conventions.
 */
export const CREATE_OFFER_MESSAGES = {
  missingField: {
    th: "กรุณากรอกข้อมูลที่จำเป็นให้ครบถ้วน",
    en: "Please fill in all required fields.",
  },
  pastTeeTime: {
    th: "เวลาทีออฟต้องเป็นเวลาในอนาคต",
    en: "Tee time must be in the future.",
  },
  tipOutOfBounds: {
    th: "จำนวนทิปไม่อยู่ในช่วงที่กำหนด",
    en: "Tip amount is out of the allowed range.",
  },
  playMode: {
    th: "กรุณาเลือกรูปแบบการเล่น (รถกอล์ฟ หรือ เดิน)",
    en: "Please choose a play mode (cart or walking).",
  },
  generic: {
    th: "เกิดข้อผิดพลาดในการสร้างรายการ กรุณาลองใหม่อีกครั้ง",
    en: "Something went wrong creating the offer. Please try again.",
  },
};

/**
 * Extract the backend error payload from an axios error, if present.
 * @param {any} error
 * @returns {{code?: string, message?: {en?: string, th?: string}}|null}
 */
export function getBackendError(error) {
  const data = error?.response?.data;
  if (data && typeof data === "object" && data.error) {
    return data.error;
  }
  return null;
}

/**
 * Classify a backend create-offer error into one of the known reasons so a
 * bilingual fallback message can be shown when the backend omits a localized
 * message. Uses the English message text (stable, server-authored) to detect
 * the specific validation case.
 * @param {{code?: string, message?: {en?: string, th?: string}}|null} backendError
 * @returns {"missingField"|"pastTeeTime"|"tipOutOfBounds"|"playMode"|"generic"}
 */
export function classifyCreateError(backendError) {
  if (!backendError) return "generic";
  const en = (backendError.message?.en || "").toLowerCase();

  if (en.includes("tee time") && en.includes("future")) {
    return "pastTeeTime";
  }
  if (en.includes("tip_amount") || en.includes("tip amount")) {
    return "tipOutOfBounds";
  }
  if (en.includes("play_mode") || en.includes("play mode")) {
    return "playMode";
  }
  if (
    backendError.code === "VALIDATION_ERROR" &&
    (en.includes("required") ||
      en.includes("missing") ||
      en.includes("not a valid"))
  ) {
    return "missingField";
  }
  return "generic";
}

/**
 * Produce a user-facing bilingual message string for a create-offer error.
 * Prefers the backend's localized Thai message (which may include the exact
 * configured bound), otherwise falls back to a classified local message.
 * Returned as "TH (EN)" to match the app's bilingual surfaces.
 * @param {any} error - axios error
 * @returns {string}
 */
export function mapCreateOfferError(error) {
  const backendError = getBackendError(error);

  if (backendError?.message?.th) {
    const th = backendError.message.th;
    const en = backendError.message.en;
    return en && en !== th ? `${th} (${en})` : th;
  }

  const reason = classifyCreateError(backendError);
  const { th, en } = CREATE_OFFER_MESSAGES[reason];
  return `${th} (${en})`;
}
